import { type JSX } from 'react';
import { useEffect, useState } from 'react';

import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { Alert } from 'Components/Alert/Alert';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Text } from 'Components/Text/Text';
import { ExceptionResponse } from 'Models/Exceptions';
import { FolderVersion } from 'Models/Folders';
import { IssueResponse } from 'Models/Issues';
import { BusinessForNonUcrmUser, BusinessResponse, UseCase } from 'Models/Ucrm';

import styles from './DeleteBusinessModal.module.css';

/**
 * The props for `DeleteBusinessModal`.
 * @param business The business to delete.
 */
export interface DeleteBusinessModalProps {
    exceptionsApi: ExceptionsApi;
    issuesApi: IssuesApi;
    onHideModal: () => void;
    onSuccess: () => Promise<void>;
    business: BusinessResponse | BusinessForNonUcrmUser;
    ucrmApi: ClientUcrmApi;
    userHasFullUcrmAccess: boolean;
}

/** Renders a modal for confirming deletion of a business. */
export const DeleteBusinessModal = (props: DeleteBusinessModalProps): JSX.Element => {
    const [failureMessage, setFailureMessage] = useState<string>();
    const [loadingExceptions, setLoadingExceptions] = useState(true);
    const [loadingFolders, setLoadingFolders] = useState(true);
    const [loadingIssues, setLoadingIssues] = useState(true);
    const [loadingUseCases, setLoadingUseCases] = useState(true);
    const [exceptions, setExceptions] = useState<ExceptionResponse[]>([]);
    const [folders, setFolders] = useState<FolderVersion[]>([]);
    const [issues, setIssues] = useState<IssueResponse[]>([]);
    const [useCases, setUseCases] = useState<UseCase[]>([]);

    useEffect(() => {
        /** Get the business's exceptions. */
        const getExceptions = async () => {
            try {
                const response = await props.exceptionsApi.getExceptionsByOrganization(props.business.id);
                setExceptions(response.data);
            } catch (error) {
                setFailureMessage(error.message);
            } finally {
                setLoadingExceptions(false);
            }
        };

        /** Get the business's folders. */
        const getFolders = async () => {
            try {
                const response = await props.ucrmApi.getAllCurrentFoldersForBusiness(props.business.id);
                setFolders(response.data);
            } catch (error) {
                setFailureMessage(error.message);
            } finally {
                setLoadingFolders(false);
            }
        };

        /** Get the business's issues. */
        const getIssues = async () => {
            try {
                const response = await props.issuesApi.getIssuesByOrganization(props.business.id);
                setIssues(response.data);
            } catch (error) {
                setFailureMessage(error.message);
            } finally {
                setLoadingIssues(false);
            }
        };

        /** Get the business's use cases. */
        const getUseCases = async () => {
            try {
                const response = await props.ucrmApi.getUseCases(props.business.id);
                setUseCases(response.data);
            } catch (error) {
                setFailureMessage(error.message);
            } finally {
                setLoadingUseCases(false);
            }
        };

        // Don't bother making API calls if the user doesn't have access to the data.
        if (props.userHasFullUcrmAccess) {
            getExceptions();
            getFolders();
            getIssues();
            getUseCases();
        } else {
            setLoadingExceptions(false);
            setLoadingFolders(false);
            setLoadingIssues(false);
            setLoadingUseCases(false);
        }
    }, [props.exceptionsApi, props.issuesApi, props.business.id, props.ucrmApi, props.userHasFullUcrmAccess]);

    /**
     * Renders the HTML message for PRIVILEGED users (i.e., those WITH the admin or UCRM roles).
     * Privileged users have access to view all UCRM data, so this lists specific data that will be deleted.
     */
    const renderPrivilegedUserMessage = (): JSX.Element => {
        return (
            <ul className={styles.deleteConfirmationList}>
                <li>
                    <Text>The business record will be deleted.</Text>
                </li>
                {useCases.length > 0 && (
                    <li>
                        <Text>All of the business's use cases will be deleted:</Text>
                        <ul>
                            {useCases.map((useCase) => (
                                <li key={useCase.id}>
                                    <Text>{useCase.name}</Text>
                                </li>
                            ))}
                        </ul>
                    </li>
                )}
                {folders.length > 0 && (
                    <li>
                        <Text>All of the business's folders and documents will be deleted:</Text>
                        <ul>
                            {folders.map((folder) => (
                                <li key={folder.id}>
                                    <Text>{folder.name}</Text>
                                </li>
                            ))}
                        </ul>
                    </li>
                )}
                {issues.length > 0 && (
                    <li>
                        <Text>All issues mapped to the business will be deleted:</Text>
                        <ul>
                            {issues.map((issue) => (
                                <li key={issue.id}>
                                    <Text>{issue.title}</Text>
                                </li>
                            ))}
                        </ul>
                    </li>
                )}
                {exceptions.length > 0 && (
                    <li>
                        <Text>All exceptions mapped to the business will be deleted:</Text>
                        <ul>
                            {exceptions.map((exception) => (
                                <li key={exception.id}>
                                    <Text>{exception.title}</Text>
                                </li>
                            ))}
                        </ul>
                    </li>
                )}
            </ul>
        );
    };

    /**
     * Renders the HTML message for UNPRIVILEGED users (i.e., those WITHOUT the admin or UCRM roles).
     * Unprivileged users do not have access to view all UCRM data, so this shows a more generic message.
     * Note: While unprivileged users cannot view all UCRM data, they are still allowed to delete businesses that are in "draft" status. It's unlikely, but draft businesses can potentially have issues, exceptions, and folders that will be deleted along with the business.
     */
    const renderUnprivilegedUserMessage = (): JSX.Element => {
        return (
            <ul className={styles.deleteConfirmationList}>
                <li>
                    <Text>The business record will be deleted.</Text>
                </li>
                <li>
                    <Text>All of the business's use cases will be deleted.</Text>
                </li>
                <li>
                    <Text>All of the business's folders and documents will be deleted.</Text>
                </li>
                <li>
                    <Text>All issues mapped to the business will be deleted.</Text>
                </li>
                <li>
                    <Text>All exceptions mapped to the business will be deleted.</Text>
                </li>
            </ul>
        );
    };

    if (loadingExceptions || loadingFolders || loadingIssues || loadingUseCases) {
        return <ConfirmationModal isLoading headerText="Delete Business" hideModal={props.onHideModal} failureMessage={failureMessage} />;
    } else {
        return (
            <ConfirmationModal
                operationType={ConfirmationModalOperationType.DELETE_AFTER_TYPING_TO_CONFIRM}
                headerText="Delete Business"
                areYouSureText={`Are you sure you want to delete "${props.business.name}"?`}
                performOperation={async () => {
                    await props.ucrmApi.deleteBusiness(props.business.id);
                    await props.onSuccess();
                    return 'Business deleted.';
                }}
                hideModal={props.onHideModal}
            >
                {failureMessage && <Alert type="error">{failureMessage}</Alert>}
                {props.userHasFullUcrmAccess && renderPrivilegedUserMessage()}
                {!props.userHasFullUcrmAccess && renderUnprivilegedUserMessage()}
            </ConfirmationModal>
        );
    }
};
